import {
  Availability,
  ChooseType,
  CHOOSETYPES_THAT_SUPPORT_SINGLE_FLOOR_SPACE,
  FloorType,
  OfferType,
  SearchModel,
  type SerializedSearchModel,
} from '@smg-real-estate/hg-search-libs';

import type {
  SearchBarActions,
  SearchBarGetters,
  SearchBarModule,
  SearchBarMutations,
  SearchBarState,
} from '@/store/types/search-bar-state';

export const initialState = (): SearchBarState => ({
  searchModel: new SearchModel(),
});

function deserialize(searchModel: SearchModel | SerializedSearchModel) {
  return searchModel instanceof SearchModel ? searchModel : SearchModel.deserialize(searchModel);
}

export const getters: SearchBarGetters = {
  offerType({ searchModel }): OfferType {
    return deserialize(searchModel).offerType!;
  },
  chooseType({ searchModel }): ChooseType {
    return deserialize(searchModel).chooseType!;
  },
  hasFromYearlyRentPerSqm({ searchModel }): boolean {
    return deserialize(searchModel).yearlyRentPerSqmRange?.from !== undefined;
  },
  hasToYearlyRentPerSqm({ searchModel }): boolean {
    return deserialize(searchModel).yearlyRentPerSqmRange?.to !== undefined;
  },
  searchModel({ searchModel }) {
    return !searchModel ? new SearchModel() : deserialize(searchModel);
  },
  isCommercialSearch({ searchModel }) {
    return deserialize(searchModel).isCommercialSearch();
  },
  hasFromPrice({ searchModel }) {
    return deserialize(searchModel).priceRange?.from !== undefined;
  },
  hasFromRooms({ searchModel }) {
    return deserialize(searchModel).numberOfRooms?.from !== undefined;
  },
  hasFromSurfaceUsable({ searchModel }) {
    return deserialize(searchModel).surfaceUsableRange?.from !== undefined;
  },
  hasToSurfaceUsable({ searchModel }) {
    return deserialize(searchModel).surfaceUsableRange?.to !== undefined;
  },
  hasToPrice({ searchModel }) {
    return deserialize(searchModel).priceRange?.to !== undefined;
  },
  hasToRooms({ searchModel }) {
    return deserialize(searchModel).numberOfRooms?.to !== undefined;
  },
  buildYearFrom({ searchModel }) {
    const buildYearFrom = deserialize(searchModel).buildYearRange?.from;
    return buildYearFrom === undefined ? '' : buildYearFrom.toString();
  },
  buildYearTo({ searchModel }) {
    const buildYearTo = deserialize(searchModel).buildYearRange?.to;
    return buildYearTo === undefined ? '' : buildYearTo.toString();
  },
  priceFrom({ searchModel }) {
    const priceFrom = deserialize(searchModel).priceRange?.from;
    return priceFrom === undefined ? '' : priceFrom.toString();
  },
  priceTo({ searchModel }) {
    const priceTo = deserialize(searchModel).priceRange?.to;
    return priceTo === undefined ? '' : priceTo.toString();
  },
  hasIsPriceDefined({ searchModel }) {
    return deserialize(searchModel).isPriceDefined ?? false;
  },
  yearlyRentPerSqmFrom({ searchModel }) {
    const yearlyRentPerSqmFrom = deserialize(searchModel).yearlyRentPerSqmRange?.from;
    return yearlyRentPerSqmFrom === undefined ? '' : yearlyRentPerSqmFrom.toString();
  },
  yearlyRentPerSqmTo({ searchModel }) {
    const yearlyRentPerSqmTo = deserialize(searchModel).yearlyRentPerSqmRange?.to;
    return yearlyRentPerSqmTo === undefined ? '' : yearlyRentPerSqmTo.toString();
  },
  roomsFrom({ searchModel }) {
    const roomsFrom = deserialize(searchModel).numberOfRooms?.from;
    return roomsFrom === undefined ? '' : roomsFrom.toString();
  },
  roomsTo({ searchModel }) {
    const roomsTo = deserialize(searchModel).numberOfRooms?.to;
    return roomsTo === undefined ? '' : roomsTo.toString();
  },
  surfaceLivingFrom({ searchModel }) {
    const surfaceLivingFrom = deserialize(searchModel).surfaceLivingRange?.from;
    return surfaceLivingFrom === undefined ? '' : surfaceLivingFrom.toString();
  },
  surfaceLivingTo({ searchModel }) {
    const surfaceLivingTo = deserialize(searchModel).surfaceLivingRange?.to;
    return surfaceLivingTo === undefined ? '' : surfaceLivingTo.toString();
  },
  surfacePropertyFrom({ searchModel }) {
    const surfacePropertyFrom = deserialize(searchModel).surfacePropertyRange?.from;
    return surfacePropertyFrom === undefined ? '' : surfacePropertyFrom.toString();
  },
  surfacePropertyTo({ searchModel }) {
    const surfacePropertyTo = deserialize(searchModel).surfacePropertyRange?.to;
    return surfacePropertyTo === undefined ? '' : surfacePropertyTo.toString();
  },
  surfaceUsableFrom({ searchModel }) {
    const surfaceUsableFrom = deserialize(searchModel).surfaceUsableRange?.from;
    return surfaceUsableFrom === undefined ? '' : surfaceUsableFrom.toString();
  },
  surfaceUsableTo({ searchModel }) {
    const surfaceUsableTo = deserialize(searchModel).surfaceUsableRange?.to;
    return surfaceUsableTo === undefined ? '' : surfaceUsableTo.toString();
  },
  volumeFrom({ searchModel }) {
    const volumeFrom = deserialize(searchModel).volumeRange?.from;
    return volumeFrom === undefined ? '' : volumeFrom.toString();
  },
  volumeTo({ searchModel }) {
    const volumeTo = deserialize(searchModel).volumeRange?.to;
    return volumeTo === undefined ? '' : volumeTo.toString();
  },
};

export const mutations: SearchBarMutations = {
  locationsChanged(state, geoLocations): void {
    state.searchModel = deserialize(state.searchModel).withLocations(geoLocations.map((g) => g.id));
  },
  setSearchModel(state, value) {
    state.searchModel = value.clone({});
  },
  setBuildYearFrom(state, value) {
    const v = value ? parseFloat(value.replace(/,/g, '.')) : NaN;
    state.searchModel = deserialize(state.searchModel).withBuildYearFrom(
      isNaN(v) ? undefined : Math.round(v),
    );
  },
  setBuildYearTo(state, value) {
    const v: number = value ? parseFloat(value.replace(/,/g, '.')) : NaN;
    state.searchModel = deserialize(state.searchModel).withBuildYearTo(
      isNaN(v) ? undefined : Math.round(v),
    );
  },
  setPriceFrom(state, value) {
    const v: number = value ? parseInt(value, 10) : NaN;
    state.searchModel = deserialize(state.searchModel).withPriceFrom(isNaN(v) ? undefined : v);
  },
  setPriceTo(state, value) {
    const v: number = value ? parseInt(value, 10) : NaN;
    state.searchModel = deserialize(state.searchModel).withPriceTo(isNaN(v) ? undefined : v);
  },
  setIsPriceDefined(state, value) {
    state.searchModel = deserialize(state.searchModel).withPriceDefined(value);
  },
  setYearlyRentPerSqmFrom(state, value) {
    const v: number = value ? parseInt(value, 10) : NaN;
    state.searchModel = deserialize(state.searchModel).withYearlyRentPerSqmFrom(
      isNaN(v) ? undefined : v,
    );
  },
  setYearlyRentPerSqmTo(state, value) {
    const v: number = value ? parseInt(value, 10) : NaN;
    state.searchModel = deserialize(state.searchModel).withYearlyRentPerSqmTo(
      isNaN(v) ? undefined : v,
    );
  },
  setRoomsFrom(state, value) {
    const v: number = value ? parseFloat(value.replace(/,/g, '.')) : NaN;
    state.searchModel = deserialize(state.searchModel).withNumberOfRoomsFrom(
      isNaN(v) ? undefined : Math.round(2 * v) / 2,
    );
  },
  setRoomsTo(state, value) {
    const v: number = value ? parseFloat(value.replace(/,/g, '.')) : NaN;
    state.searchModel = deserialize(state.searchModel).withNumberOfRoomsTo(
      isNaN(v) ? undefined : Math.round(2 * v) / 2,
    );
  },
  setSurfaceLivingFrom(state, value) {
    const v: number = value ? parseFloat(value.replace(/,/g, '.')) : NaN;
    state.searchModel = deserialize(state.searchModel).withSurfaceLivingFrom(
      isNaN(v) ? undefined : Math.round(v),
    );
  },
  setSurfaceLivingTo(state, value) {
    const v: number = value ? parseFloat(value.replace(/,/g, '.')) : NaN;
    state.searchModel = deserialize(state.searchModel).withSurfaceLivingTo(
      isNaN(v) ? undefined : Math.round(v),
    );
  },
  setSurfacePropertyFrom(state, value) {
    const v: number = value ? parseFloat(value.replace(/,/g, '.')) : NaN;
    state.searchModel = deserialize(state.searchModel).withSurfacePropertyFrom(
      isNaN(v) ? undefined : Math.round(v),
    );
  },
  setSurfacePropertyTo(state, value) {
    const v: number = value ? parseFloat(value.replace(/,/g, '.')) : NaN;
    state.searchModel = deserialize(state.searchModel).withSurfacePropertyTo(
      isNaN(v) ? undefined : Math.round(v),
    );
  },
  setSurfaceUsableFrom(state, value) {
    const v: number = value ? parseFloat(value.replace(/,/g, '.')) : NaN;
    state.searchModel = deserialize(state.searchModel).withSurfaceUsableFrom(
      isNaN(v) ? undefined : Math.round(v),
    );
  },
  setSurfaceUsableTo(state, value) {
    const v: number = value ? parseFloat(value.replace(/,/g, '.')) : NaN;
    state.searchModel = deserialize(state.searchModel).withSurfaceUsableTo(
      isNaN(v) ? undefined : Math.round(v),
    );
  },
  setVolumeFrom(state, value) {
    const v: number = value ? parseFloat(value.replace(/,/g, '.')) : NaN;
    state.searchModel = deserialize(state.searchModel).withVolumeFrom(
      isNaN(v) ? undefined : Math.round(v),
    );
  },
  setVolumeTo(state, value) {
    const v: number = value ? parseFloat(value.replace(/,/g, '.')) : NaN;
    state.searchModel = deserialize(state.searchModel).withVolumeTo(
      isNaN(v) ? undefined : Math.round(v),
    );
  },
  setRooms(state, range) {
    const roomsFrom = range.from === undefined ? undefined : Math.round(range.from * 2) / 2;
    const roomsTo = range.to === undefined ? undefined : Math.round(range.to * 2) / 2;
    const searchModel = deserialize(state.searchModel);
    const currentFrom = searchModel.numberOfRooms?.from;
    const currentTo = searchModel.numberOfRooms?.to;
    if (currentFrom !== roomsFrom || currentTo !== roomsTo) {
      state.searchModel = searchModel.withNumberOfRoomsFrom(roomsFrom).withNumberOfRoomsTo(roomsTo);
    }
  },
  setPrices(state, range) {
    const from = range.from === undefined ? undefined : Math.round(range.from);
    const to = range.to === undefined ? undefined : Math.round(range.to);
    const searchModel = deserialize(state.searchModel);
    const currentFrom = searchModel.priceRange?.from;
    const currentTo = searchModel.priceRange?.to;
    if (currentFrom !== from || currentTo !== to) {
      state.searchModel = searchModel.withPriceFrom(from).withPriceTo(to);
    }
  },
  setYearlyRentPerSqm(state, range) {
    const from = range.from === undefined ? undefined : Math.round(range.from);
    const to = range.to === undefined ? undefined : Math.round(range.to);
    const searchModel = deserialize(state.searchModel);
    const currentFrom = searchModel.yearlyRentPerSqmRange?.from;
    const currentTo = searchModel.yearlyRentPerSqmRange?.to;
    if (currentFrom !== from || currentTo !== to) {
      state.searchModel = searchModel.withYearlyRentPerSqmFrom(from).withYearlyRentPerSqmTo(to);
    }
  },
  setSurfaceUsable(state, range) {
    const surfaceUsableFrom = range.from === undefined ? undefined : Math.round(range.from * 2) / 2;
    const surfaceUsableTo = range.to === undefined ? undefined : Math.round(range.to * 2) / 2;
    const searchModel = deserialize(state.searchModel);
    const currentFrom = searchModel.surfaceUsableRange?.from;
    const currentTo = searchModel.surfaceUsableRange?.to;
    if (currentFrom !== surfaceUsableFrom || currentTo !== surfaceUsableTo) {
      state.searchModel = searchModel
        .withSurfaceUsableFrom(surfaceUsableFrom)
        .withSurfaceUsableTo(surfaceUsableTo);
    }
  },
  setChooseType(state, chooseType) {
    state.searchModel = deserialize(state.searchModel)
      .withObjectTypes([])
      .withChooseType(chooseType);
  },
  objectTypesChanged(state, objectTypes) {
    state.searchModel = deserialize(state.searchModel).withObjectTypes(objectTypes);
  },
  radiusChanged(state, radius) {
    state.searchModel = deserialize(state.searchModel).withRadius(radius);
  },
  availabilityChanged(state, availability: Availability | undefined) {
    state.searchModel = deserialize(state.searchModel).withAvailability(availability);
  },
  offerTypeChanged(state, offerType) {
    state.searchModel = deserialize(state.searchModel).withOfferType(offerType);
  },
  floorTypeChanged(state, floorType: FloorType) {
    state.searchModel = deserialize(state.searchModel).withFloor(floorType);
  },
  surfaceLivingRangeChanged(state, numberRange) {
    state.searchModel = deserialize(state.searchModel).withSurfaceLivingRange(numberRange);
  },
  surfaceUsableRangeChanged(state, numberRange) {
    state.searchModel = deserialize(state.searchModel).withSurfaceUsableRange(numberRange);
  },
  surfacePropertyRangeChanged(state, numberRange) {
    state.searchModel = deserialize(state.searchModel).withSurfacePropertyRange(numberRange);
  },
  buildYearRangeChanged(state, numberRange) {
    state.searchModel = deserialize(state.searchModel).withBuildYearRange(numberRange);
  },
  volumeRangeChanged(state, numberRange) {
    state.searchModel = deserialize(state.searchModel).withVolumeRange(numberRange);
  },
  textFieldChanged(state, textSearch) {
    if (textSearch?.query) {
      state.searchModel = deserialize(state.searchModel).withText(
        textSearch?.query,
        textSearch?.language,
      );
    } else {
      state.searchModel = deserialize(state.searchModel).withText();
    }
  },
};

export const actions: SearchBarActions = {
  updateChooseType(context, chooseType: ChooseType): void {
    const oldChooseType = context.state.searchModel.chooseType;

    context.commit('setChooseType', chooseType);

    if (context.getters.offerType === OfferType.BUY) {
      return;
    }

    if (
      CHOOSETYPES_THAT_SUPPORT_SINGLE_FLOOR_SPACE.includes(chooseType) !==
      (oldChooseType && CHOOSETYPES_THAT_SUPPORT_SINGLE_FLOOR_SPACE.includes(oldChooseType))
    ) {
      context.commit('setPrices', { from: undefined, to: undefined });
      context.commit('setYearlyRentPerSqm', { from: undefined, to: undefined });
    }
  },
};

export default {
  namespaced: true,
  state: initialState,
  mutations,
  getters,
  actions,
} as SearchBarModule;
